import { RadioField } from 'app/components/FormFields/RadioField';
import { Select } from 'app/components/FormFields/Select';
import {
  DESIRED_LOAN_TERM_OPTIONS,
  VEHICLE_TYPE_OPTIONS,
} from 'app/models/options/options';
import { Question } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import { Currency } from '../../components/FormFields/Currency';
import { SHARED } from 'app/models/questions/shared';
import { TextInput } from '../../components/FormFields/TextInput';
import { OV_INQUIRY_FINAL_PAGE_QUESTION } from '../sections/constants';
import { OtherVehicleConfirmation } from 'app/components/OvLoan/OtherVehicleConfirmation';

/**
 * Takes in a field name and returns the corresponding other vehicle loan
 * question
 *
 * @param {string} name Field name
 * @returns {Question} Question with matching field name
 */
export const ovInquiryQuestionByName = (name: string): Question => {
  return OV_INQUIRY_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

/**
 * Takes in an array of field names and returns an array of questions
 *
 * @param {string[]} [names=[]] Field names
 * @returns {Question[]} Questions matching field names
 */
export const ovInquiryQuestionsByName = (names: string[] = []): Question[] => {
  const questions: Question[] = [];
  for (const name of names) {
    questions.push(ovInquiryQuestionByName(name));
  }
  return questions;
};

export const ovInquiryQuestionById = (id: number): Question => {
  return OV_INQUIRY_QUESTIONS.find((question) => question.id === id) as Question;
};

const OV_INQUIRY_QUESTIONS: Question[] = [
  {
    id           : 1000,
    component    : TextInput,
    name         : FieldNames.vehicle,
    label        : 'Year, Make & Model',
  },
  {
    id           : 1001,
    component    : RadioField,
    name         : FieldNames.vehicleType,
    label        : 'Vehicle Type',
    options      : VEHICLE_TYPE_OPTIONS,
    title        : 'Tell us about the vehicle you plan to purchase.',
  },
  {
    id           : 1002,
    component    : Currency,
    label        : 'Purchase Price',
    name         : FieldNames.purchasePrice,
    title        : 'What is the purchase price?',
    popover      : "Enter the total price you expect to pay for your vehicle.",
  },
  {
    id           : 1003,
    component    : Currency,
    label        : 'Down Payment',
    name         : FieldNames.downDollar,
    title        : 'What is your down payment?',
    isOptional   : true,
    popover      : "Enter the amount of money you expect to put down for the vehicle purchase, separate from the trade-in value.",
    validator    : {
      type: 'isAutoLoanValid',
      param: FieldNames.downDollar,
    },
  },
  {
    id           : 1004,
    component    : Select,
    name         : FieldNames.loanTermDesired,
    options      : DESIRED_LOAN_TERM_OPTIONS,
    title        : 'What is your desired loan term/length (in months)?',
  },
  {
    id: 1005,
    ...SHARED[FieldNames.firstName],
  },
  {
    id: 1006,
    ...SHARED[FieldNames.middleName],
  },
  {
    id: 1007,
    ...SHARED[FieldNames.lastName],
  },
  {
    id: 1008,
    ...SHARED[FieldNames.suffixName],
  },
  {
    id: 1009,
    ...SHARED[FieldNames.email],
  },
  {
    id: 1010,
    ...SHARED[FieldNames.phone],
  },
  {
    id: 1011,
    ...SHARED[FieldNames.dateOfBirth],
  },
  {
    id          : OV_INQUIRY_FINAL_PAGE_QUESTION,
    component   : OtherVehicleConfirmation,
    name        : 'FinalPage',
  },
];
